import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Check, X, AlertTriangle } from 'lucide-react';
import { strategiesAPI, positionsAPI } from '@/services/api';
import { actionsAPI } from '@/services/apolloApi';

const PHASES = [
  { key: 'stopping', label: 'Stopping strategy' },
  { key: 'liquidating', label: 'Liquidating positions' },
  { key: 'dwelling', label: 'Waiting for settlement' },
  { key: 'applying', label: 'Applying changes' },
  { key: 'restarting', label: 'Restarting strategy' },
  { key: 'confirming', label: 'Confirming planned orders' },
];

export default function ActionExecutionModal({ open, onClose, action, paramOverrides }) {
  const [phase, setPhase] = useState(null); // null = review, then phase keys, 'complete', 'error'
  const [dwellCountdown, setDwellCountdown] = useState(5);
  const [error, setError] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPhase(null);
      setDwellCountdown(5);
      setError(null);
      setConfirmResult(null);
    }
  }, [open]);

  if (!open || !action) return null;

  const strategyId = action.strategyId || action.target_id;
  const isRunning = action.strategyStatus === 'running' || action.strategyStatus === 'analyzing';

  async function executeFlow() {
    try {
      if (isRunning) {
        // Phase 1: Stop
        setPhase('stopping');
        await strategiesAPI.stop(strategyId);

        // Phase 2: Liquidate
        setPhase('liquidating');
        try {
          await strategiesAPI.liquidate(strategyId);
        } catch (liqErr) {
          // Liquidate may fail if no positions — continue
        }
        // Poll for position closure (max 30s)
        await new Promise((resolve) => {
          let elapsed = 0;
          pollRef.current = setInterval(async () => {
            elapsed += 2000;
            try {
              const positions = await positionsAPI.getByStrategy(strategyId);
              const openPositions = (positions || []).filter((p) => p.status === 'open');
              if (openPositions.length === 0 || elapsed >= 30000) {
                clearInterval(pollRef.current);
                resolve();
              }
            } catch {
              clearInterval(pollRef.current);
              resolve();
            }
          }, 2000);
        });

        // Phase 3: Dwell (5 seconds)
        setPhase('dwelling');
        for (let i = 5; i > 0; i--) {
          setDwellCountdown(i);
          await new Promise((r) => setTimeout(r, 1000));
        }
      }

      // Phase 4: Apply changes
      setPhase('applying');
      const overrides = paramOverrides || {};
      // Build merged params if we have overrides
      let payloadOverrides = {};
      if (action.actionType === 'adjust_params' && Object.keys(overrides).length > 0) {
        const currentParams = action.currentParams || action.payload?.previousParams || {};
        payloadOverrides = { mergedParams: { ...currentParams, ...action.proposedChanges, ...overrides } };
      }
      await actionsAPI.execute(action.actionId || action.id, payloadOverrides);

      if (isRunning) {
        // Phase 5: Restart
        setPhase('restarting');
        await strategiesAPI.start(strategyId);

        // Phase 6: Confirm
        setPhase('confirming');
        await new Promise((r) => setTimeout(r, 2000)); // Wait for executor to pick up
        try {
          const strategy = await strategiesAPI.getById(strategyId);
          setConfirmResult({
            status: strategy.status,
            success: strategy.status === 'running',
          });
        } catch {
          setConfirmResult({ status: 'unknown', success: true });
        }
      }

      setPhase('complete');
    } catch (err) {
      setError(err.message);
      setPhase('error');
    }
  }

  const phaseIndex = PHASES.findIndex((p) => p.key === phase);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={phase === 'complete' || phase === 'error' ? onClose : undefined} />
      <div className="relative bg-card border border-white/10 rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-sm font-semibold text-foreground">
            {phase === null ? 'Apply Strategy Changes' : phase === 'complete' ? 'Changes Applied' : phase === 'error' ? 'Error' : 'Applying Changes...'}
          </h3>
          {(phase === null || phase === 'complete' || phase === 'error') && (
            <button onClick={onClose} className="p-1 rounded text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="px-5 py-4">
          {/* Review phase */}
          {phase === null && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                {isRunning
                  ? 'This strategy is currently running. It will be stopped, positions liquidated, changes applied, then restarted.'
                  : 'Changes will be applied directly to the stopped strategy.'}
              </p>

              {isRunning && (
                <div className="space-y-1">
                  {PHASES.map((p) => (
                    <div key={p.key} className="flex items-center gap-2 text-xs text-muted-foreground/60">
                      <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />
                      {p.label}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 py-2 text-xs rounded-lg border border-white/10 text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeFlow}
                  className="flex-1 py-2 text-xs rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
                >
                  {isRunning ? 'Stop, Apply & Restart' : 'Apply Changes'}
                </button>
              </div>
            </div>
          )}

          {/* Executing phases */}
          {phase && phase !== 'complete' && phase !== 'error' && (
            <div className="space-y-2">
              {PHASES.map((p, i) => {
                const isActive = p.key === phase;
                const isDone = i < phaseIndex;
                const isPending = i > phaseIndex;
                if (!isRunning && ['stopping', 'liquidating', 'dwelling', 'restarting', 'confirming'].includes(p.key)) return null;
                return (
                  <div key={p.key} className={`flex items-center gap-2.5 text-xs ${isActive ? 'text-foreground' : isDone ? 'text-emerald-400' : 'text-muted-foreground/40'}`}>
                    {isDone ? (
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                    ) : isActive ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded-full border border-white/10" />
                    )}
                    <span>{p.label}</span>
                    {isActive && p.key === 'dwelling' && (
                      <span className="text-amber-400 font-mono ml-auto">{dwellCountdown}s</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Complete */}
          {phase === 'complete' && (
            <div className="text-center py-4">
              <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground mb-1">Changes applied successfully</p>
              {confirmResult?.status === 'running' && (
                <p className="text-xs text-emerald-400">Strategy is running. Planned orders queued.</p>
              )}
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 text-xs rounded-lg bg-accent/10 text-foreground hover:bg-accent/20 transition-colors"
              >
                Done
              </button>
            </div>
          )}

          {/* Error */}
          {phase === 'error' && (
            <div className="text-center py-4">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground mb-1">Something went wrong</p>
              <p className="text-xs text-red-400">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 text-xs rounded-lg bg-accent/10 text-foreground hover:bg-accent/20 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
